/**
 * YG Hero Image - Popup Manager
 * Handles popup cards, sticky navigation, and product tabs
 */

(function () {
  'use strict';

  class PopupManager {
    constructor() {
      this.swipers = {};
      this.init();
    }

    init() {
      this.movePopupsToBody();
      this.moveStickyContainerToBody();
      this.setupPopupTriggers();
      this.setupStickyCards();
      this.setupScrollObserver();
      this.setupProductTabs();

      this.initT4SContentAnimation();
    }

    movePopupsToBody() {
      // 将所有弹窗移动到 body 的最外层，避免被父元素的 z-index 和 overflow 限制
      const popups = document.querySelectorAll('.yg-popup-container');
      popups.forEach(popup => {
        if (popup.parentElement !== document.body) {
          document.body.appendChild(popup);
        }
      });
    }

    moveStickyContainerToBody() {
      // 将 sticky container 移动到 body 的最外层，与 header 同级
      const stickyContainer = document.querySelector('.yg-popup-cards-sticky-container');
      if (stickyContainer && stickyContainer.parentElement !== document.body) {
        document.body.appendChild(stickyContainer);
      }
    }

    setupPopupTriggers() {
      // Handle all popup triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-popup-trigger]');
        if (trigger) {
          e.preventDefault();
          const popupId = trigger.getAttribute('data-popup-trigger');
          this.openPopup(popupId);
        }

        // Close popup on overlay click
        if (e.target.classList.contains('yg-popup-overlay')) {
          this.closePopup(e.target.closest('.yg-popup-container'));
        }

        // Close popup on close button click
        const closeBtn = e.target.closest('.yg-popup-close');
        if (closeBtn) {
          this.closePopup(closeBtn.closest('.yg-popup-container'));
        }
      });

      // Close popup on ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const activePopup = document.querySelector('.yg-popup-container.is-active');
          if (activePopup) {
            this.closePopup(activePopup);
          }
        }
      });
    }

    openPopup(popupId) {
      const popup = document.getElementById(popupId);
      if (popup) {
        popup.classList.add('is-active');
        document.body.style.overflow = 'hidden';

        // Initialize Swiper for product popup if needed
        if (popup.classList.contains('yg-product-popup-container')) {
          this.initProductSwipers(popup);
        }
      }
    }

    closePopup(popup) {
      if (popup) {
        popup.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    }

    setupStickyCards() {
      const cards = document.querySelectorAll('.yg-popup-card');
      const stickyContainer = document.querySelector('.yg-popup-cards-sticky-container');

      if (!stickyContainer || cards.length === 0) return;

      // Track if we've scrolled past the threshold where header changes
      let hasScrolledPastThreshold = false;
      const scrollThreshold = 100; // Adjust based on your header behavior

      // Get header height with scroll state awareness
      const getHeaderHeight = () => {
        const header =
          document.querySelector('header') ||
          document.querySelector('.header') ||
          document.querySelector('[role="banner"]') ||
          document.querySelector('.t4s-header');

        if (header) {
          // Check if header is hidden by various methods
          const headerSection = header.closest('.shopify-section');
          const computedStyle = window.getComputedStyle(header);
          
          // Check multiple conditions for hidden header
          const isHidden = 
            header.classList.contains('shopify-section-header-hidden') ||
            header.classList.contains('header-hidden') ||
            header.classList.contains('is-hidden') ||
            (headerSection && headerSection.classList.contains('shopify-section-header-hidden')) ||
            computedStyle.display === 'none' ||
            computedStyle.visibility === 'hidden' ||
            computedStyle.opacity === '0' ||
            header.offsetHeight === 0;
          
          if (isHidden) {
            return 0;
          }
          
          // Check current scroll position
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
          
          // If we're scrolled past threshold, update the flag
          if (currentScroll > scrollThreshold) {
            hasScrolledPastThreshold = true;
          } else {
            hasScrolledPastThreshold = false;
          }
          
          // Return actual visible height
          const height = header.offsetHeight;
          return height;
        }
        return 0;
      };

      // Set sticky container position using CSS variable
      let lastHeaderHeight = 0;
      const updateStickyPosition = () => {
        const headerHeight = getHeaderHeight();
        // Only update if height changed
        if (headerHeight !== lastHeaderHeight) {
          document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
          lastHeaderHeight = headerHeight;
        }
      };

      // Initial update - delay to ensure header is fully loaded
      updateStickyPosition();
      
      // Additional updates to catch header state changes during page load
      setTimeout(() => updateStickyPosition(), 100);
      setTimeout(() => updateStickyPosition(), 300);
      setTimeout(() => updateStickyPosition(), 500);

      // Update on window resize
      window.addEventListener('resize', updateStickyPosition);
      
      // Update on page load complete
      window.addEventListener('load', updateStickyPosition);

      // Update on scroll (for sticky headers) - use requestAnimationFrame for better performance
      let rafId = null;
      const handleScroll = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          updateStickyPosition();
          rafId = null;
        });
      };
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Monitor header changes with MutationObserver
      const header =
        document.querySelector('header') ||
        document.querySelector('.header') ||
        document.querySelector('[role="banner"]') ||
        document.querySelector('.t4s-header');

      if (header) {
        // Observe header class and style changes
        const headerObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
              const target = mutation.target;
              updateStickyPosition();
            }
          });
        });

        headerObserver.observe(header, {
          attributes: true,
          attributeFilter: ['class', 'style'],
          childList: false,
          subtree: false,
        });

        // Also observe header's parent (shopify-section) for class changes
        const headerSection = header.closest('.shopify-section');
        if (headerSection) {
          const sectionObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                updateStickyPosition();
              }
            });
          });

          sectionObserver.observe(headerSection, {
            attributes: true,
            attributeFilter: ['class'],
            childList: false,
            subtree: false,
          });
        }

        // Also observe header's computed style changes with ResizeObserver
        if (typeof ResizeObserver !== 'undefined') {
          const resizeObserver = new ResizeObserver(() => {
            updateStickyPosition();
          });
          resizeObserver.observe(header);
        }
      }

      // Continuous monitoring with requestAnimationFrame for smooth updates
      // Only run for first few seconds to catch initial state changes
      let animationFrameId = null;
      let monitoringStartTime = Date.now();
      const MONITORING_DURATION = 3000; // Monitor for 3 seconds after init
      
      const continuousUpdate = () => {
        updateStickyPosition();
        
        // Stop continuous monitoring after initial period
        const elapsed = Date.now() - monitoringStartTime;
        if (elapsed < MONITORING_DURATION) {
          animationFrameId = requestAnimationFrame(continuousUpdate);
        } else {
          console.log('Continuous monitoring stopped after', elapsed, 'ms');
        }
      };

      // Start continuous monitoring for initial period
      continuousUpdate();

      // Observe the main cards container instead of individual cards
      const cardsContainer = document.querySelector('.yg-popup-cards-container');

      if (!cardsContainer) return;

      // Function to initialize the intersection observer
      const initIntersectionObserver = () => {
        
        // Check if we're already scrolled on page load
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
        
        // If scrolled, trigger a scroll event to update header state
        if (currentScroll > 0) {
          // Trigger scroll event to let header update its state
          window.dispatchEvent(new Event('scroll'));
          
          // Wait a bit for header to update, then update sticky position
          setTimeout(() => {
            updateStickyPosition();
          }, 100);
        }
        
        // Final header height update before starting observer
        updateStickyPosition();
        
        // Create intersection observer for the cards container
        const containerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                // Container is visible, hide sticky version
                stickyContainer.classList.remove('is-visible');
              } else {
                // Container is not visible, show sticky version
                // Double check header height before showing
                updateStickyPosition();
                stickyContainer.classList.add('is-visible');
              }
            });
          },
          {
            threshold: 0,
            rootMargin: '0px',
          }
        );

        containerObserver.observe(cardsContainer);
      };

      // Wait for page to fully load before initializing observer
      if (document.readyState === 'complete') {
        // Page already loaded
        setTimeout(() => {
          initIntersectionObserver();
        }, 100);
      } else {
        // Wait for load event
        window.addEventListener('load', () => {
          // Add a small delay after load to ensure everything is settled
          setTimeout(() => {
            initIntersectionObserver();
          }, 200);
        });
      }
    }

    setupScrollObserver() {
      // Additional scroll handling if needed
      let ticking = false;

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            // Handle any scroll-based animations here
            ticking = false;
          });
          ticking = true;
        }
      });
    }

    setupProductTabs() {
      // Setup tab switching for product popups
      document.addEventListener('click', (e) => {
        const tabBtn = e.target.closest('.yg-product-tab-btn');
        if (!tabBtn) return;

        const popup = tabBtn.closest('.yg-product-popup-container');
        if (!popup) return;

        const tabIndex = tabBtn.getAttribute('data-tab-index');
        const allTabs = popup.querySelectorAll('.yg-product-tab-btn');
        const allPanels = popup.querySelectorAll('.yg-product-tab-panel');

        // Remove active class from all tabs and panels
        allTabs.forEach((tab) => tab.classList.remove('yg-product-tab-btn--active'));
        allPanels.forEach((panel) => panel.classList.remove('yg-product-tab-panel--active'));

        // Add active class to clicked tab and corresponding panel
        tabBtn.classList.add('yg-product-tab-btn--active');
        const activePanel = popup.querySelector(`[data-tab-panel="${tabIndex}"]`);
        if (activePanel) {
          activePanel.classList.add('yg-product-tab-panel--active');
        }
      });
    }

    initT4SContentAnimation() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        console.warn('GSAP 或 ScrollTrigger 未加载');
        return;
      }
    
      gsap.registerPlugin(ScrollTrigger);
    
      const sections = document.querySelectorAll('.t4s-content-position');
    
      sections.forEach((section) => {
        const items = section.querySelectorAll('.t4s-bl-item');
        if (!items.length) return;
    
        // 初始状态（避免闪现）
        gsap.set(items, {
          opacity: 0,
          y: 60,
          scale: 0.96
        });
    
        gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            toggleActions: 'play none none none',
            once: true,
          }
        }).to(items, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.2
        });
      });
    }

    initProductSwipers(popup) {
      // Check if on mobile (no swiper needed)
      if (window.innerWidth <= 767) return;

      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper library not loaded');
        return;
      }

      const swiperElements = popup.querySelectorAll('.yg-product-swiper');

      swiperElements.forEach((swiperEl) => {
        const swiperId = swiperEl.getAttribute('data-swiper-id');

        // Skip if already initialized
        if (this.swipers[swiperId]) return;

        // Find navigation buttons
        const prevBtn = popup.querySelector(`.yg-product-swiper-prev[data-swiper-id="${swiperId}"]`);
        const nextBtn = popup.querySelector(`.yg-product-swiper-next[data-swiper-id="${swiperId}"]`);

        // Initialize Swiper
        this.swipers[swiperId] = new Swiper(swiperEl, {
          slidesPerView: 2.5,
          spaceBetween: 20,
          navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn,
          },
          breakpoints: {
            768: {
              slidesPerView: 2.5,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 2.5,
              spaceBetween: 20,
            },
          },
        });
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new PopupManager();
    });
  } else {
    new PopupManager();
  }
})();

